import { request } from './client';

/**
 * Sending a guest a copy of the receipt they are already looking at.
 *
 * Both routes are anonymous and take no session token, which is deliberate rather than an oversight on the
 * server's part: the short code IS the authorization, and a guest reaching a receipt from a delivered link
 * may arrive days later with no session at all.
 *
 * Both are rate limited server-side under the "sensitive" policy, because repetition is the attack — without
 * it these endpoints spray email and SMS at any address somebody supplies. A 429 is therefore an expected
 * answer here, not an outage, and the caller should say so in those words.
 *
 * Both answer 204 with no body.
 */

/**
 * The short code out of the stored receipt's URL.
 *
 * The link is `{ReceiptLinkBaseUrl}/{shortCode}` and the base is configured per environment, so the last
 * segment is the code. Read from the link rather than sent as its own field because the API sends one and not
 * the other — worth knowing if `receiptLink` ever stops being a plain link.
 *
 * Returns null rather than a guess when there is nothing usable, so a caller shows no delivery controls at
 * all instead of offering a button that cannot work.
 */
export function receiptShortCode(receiptLink: string | null): string | null {
    if (!receiptLink) return null;

    // Tolerates a trailing slash and a query string; a relative link parses against the current origin, which
    // is fine because only the path is read.
    let path: string;
    try {
        path = new URL(receiptLink, window.location.origin).pathname;
    } catch {
        return null;
    }

    const code = path.split('/').filter(Boolean).pop();

    return code ? code : null;
}

export function emailReceipt(
    shortCode: string,
    emailAddress: string,
    signal?: AbortSignal,
): Promise<void> {
    return request<void>(`/receipts/${encodeURIComponent(shortCode)}/email`, {
        method: 'POST',
        body: { emailAddress },
        signal,
    });
}

export function textReceipt(shortCode: string, phoneNumber: string, signal?: AbortSignal): Promise<void> {
    return request<void>(`/receipts/${encodeURIComponent(shortCode)}/sms`, {
        method: 'POST',
        body: { phoneNumber },
        signal,
    });
}
